/* ---------------------------------------------------------------------------
   One structure's dashboard (LC-30).

   The start page is about the register as a whole (src/screens/overview.ts);
   this is the same kind of page about one bridge. Where it is in its life and
   what comes next, then each application's answer about it — the programme,
   what is open on site, the handover, the inspection cycle, the defects, the
   rating — then the verdict a road user needs, and the way into each
   application.

   EVERY TILE ANSWERS FOR THE STATE THE STRUCTURE IS IN, OR SAYS IT CANNOT. A
   bridge under construction has no inspection cycle and a standing bridge has
   no programme; a zero on either would be the register asserting something
   nobody recorded. So a tile that has no answer carries the word for why —
   *Not in service*, *No baseline*, *Not read* — where its figure would be.
   A bridge built with no works open on it reads *No works* (LC-85): the
   phase says so, whatever the site record holds.

   NOTHING HERE IS WORKED OUT AFRESH. Each figure is the one its application
   already draws, read through the same function: the forecast is
   `forecastOf`, the standing is `inspectionStanding`, the verdict is
   `operationalStatus`, the governing rating is `governingRating`. A dashboard
   that computed its own would be a fifth opinion about the same bridge.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child } from '../core/dom.ts';
import { can, whyNot, type State } from '../core/store.ts';
import { PRODUCTS, type AppDef } from '../core/apps.ts';
import { appHref } from '../core/navigate.ts';
import { panel } from '../components/panel.ts';
import { button, emptyState, fact, tag, type TagRank } from '../components/widgets.ts';
import { phaseChip, worksChip } from '../components/phaseChip.ts';
import { statusChip } from '../components/statusChip.ts';
import {
  cycleChipLabel,
  cycleLabel,
  findBridge,
  heroModel,
  inspectionStanding,
  inspectionTypeLabel,
  prettyDate,
  type CycleStanding,
  type InspectionStanding,
} from '../data/derive.ts';
import { WORKS_KIND_LABEL, accessLine, appAccess, phaseOf, stateKey, viewPhaseOf, type PhaseStateKey } from '../data/phase.ts';
import { CAUSE_LABEL, decidedLine, governingRating, operationalStatus, postingTonnes, sinceLine } from '../data/posting.ts';
import { HANDOVER_STATUS_LABEL } from '../data/handover.ts';
import { DEFECT_SEVERITIES, isOpenStatus } from '../data/defects.ts';
import { completionOf, varianceFor } from '../data/variance.ts';
import { slipWord } from '../data/forecast.ts';
import { join } from '../data/recorded.ts';
import { contractCompletion, countable, forecastOf } from './programme.ts';
import { modelDeleteCommand, modelImportCommand } from './detail.ts';
import { latestAcceptedInspection } from '../rating/damage.ts';
import { ratingDueFor, ratingsOf } from '../rating/mount.ts';
import { reratingChip } from '../rating/screens/structures.ts';
import { displayRf, displayTonnes } from '../cbplr/index.ts';
import { SITE_MAP_CREDIT } from '../map/siteMap.ts';
import type { BridgeView } from '../data/types.ts';

/** Today, as a calendar day. The one place this module reads the clock. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A tile's answer where it has none for this state: the word for why, not a zero. */
function unanswered(word: string): HTMLElement {
  return tag(word, 'none');
}

/** One tile: a panel with its answer, and the facts behind it. */
function tile(key: string, title: string, answer: Child, ...facts: Child[]): HTMLElement {
  return panel(
    { title, class: ['bdash__tile', `bdash__tile--${key}`] },
    h('div', { class: 'bdash__answer' }, answer),
    facts.length ? h('div', { class: 'det-facts' }, ...facts) : null,
  );
}

/**
 * Whether the site record on hand is this structure's, or the word for why not.
 * The start page reads it only for this screen — see `readsSiteRecord`.
 */
function siteRecord(b: BridgeView, s: Readonly<State>): true | HTMLElement {
  if (s.progressOf === b.id && s.progressRead === 'read') return true;
  if (s.progressOf === b.id && s.progressRead === 'unreachable') return unanswered('Not reachable');
  return unanswered('Not read');
}

/* --------------------------- state and next milestone --------------------------- */

const CYCLE_NAME: Readonly<Record<CycleStanding['cycle'], string>> = {
  routine: 'Routine inspection',
  principal: 'Principal inspection',
  baseline: 'Baseline inspection',
  underwater: 'Underwater inspection',
  special: 'Special inspection',
};

/**
 * What comes next: a contract milestone before handover, the inspection or
 * re-rating that falls due first after it. Closed, nothing is scheduled.
 */
function nextMilestone(b: BridgeView, s: Readonly<State>, key: PhaseStateKey, standing: InspectionStanding): Child {
  if (key === 'planned') return unanswered('No baseline');
  if (key === 'closed') return unanswered('None scheduled');
  if (key === 'construction') {
    const read = siteRecord(b, s);
    if (read !== true) return read;
    if (s.baseline === null) return unanswered('No baseline');
    const next = (s.baseline.contract?.milestones ?? [])
      .filter((m) => m.dueOn >= today())
      .sort((a, z) => a.dueOn.localeCompare(z.dueOn))[0];
    if (next) return `${next.name} · ${prettyDate(next.dueOn)}`;
    const completion = contractCompletion(s);
    if (completion !== null) return `Completion · ${prettyDate(completion)}`;
    const forecast = forecastOf(s);
    return forecast ? `Forecast completion · ${prettyDate(forecast.forecastFinish)}` : unanswered('No baseline');
  }
  const dated: { what: string; on: string }[] = [standing.baseline, standing.special, standing.routine, standing.principal, standing.underwater]
    .filter((c): c is CycleStanding => c !== null && c.due !== null)
    .map((c) => ({ what: CYCLE_NAME[c.cycle], on: c.due! }));
  const rerate = ratingDueFor(b, s.registerDefects);
  if (rerate !== null && rerate.rated > 0) {
    dated.push({ what: 'Re-rating', on: rerate.due ? today() : (rerate.dueOn ?? today()) });
  }
  const first = dated.sort((a, z) => a.on.localeCompare(z.on))[0];
  if (!first) return unanswered('None dated');
  return `${first.what} · ${first.on < today() ? `overdue since ${prettyDate(first.on)}` : prettyDate(first.on)}`;
}

function stateTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey, standing: InspectionStanding): HTMLElement {
  const p = phaseOf(b);
  return tile(
    'state',
    'State',
    h('span', { class: 'reg__state' }, phaseChip(b), worksChip(b)),
    fact('Since', p.recorded ? sinceLine(b, prettyDate) : '—'),
    fact('Next milestone', h('span', { class: 'bdash__next' }, nextMilestone(b, s, key, standing))),
  );
}

/* --------------------------- the programme --------------------------- */

/** Built, and no works open on it (LC-81): the programme and the site record have nothing current. */
function noWorks(b: BridgeView, key: PhaseStateKey): boolean {
  return key !== 'planned' && key !== 'construction' && phaseOf(b).works === null;
}

function programmeTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey): HTMLElement {
  if (key === 'planned') return tile('programme', 'Programme', unanswered('No baseline'));
  const read = siteRecord(b, s);
  /* A programme kept as history is still shown; without one, there are no works. */
  if (noWorks(b, key) && (read !== true || s.baseline === null)) return tile('programme', 'Programme', unanswered('No works'));
  if (read !== true) return tile('programme', 'Programme', read);
  if (s.baseline === null) {
    return tile('programme', 'Programme', unanswered(key === 'construction' ? 'No baseline' : 'No programme'));
  }
  const done = completionOf(varianceFor(s.baseline, s.matches, countable(s)));
  const forecast = forecastOf(s)!;
  const contract = contractCompletion(s);
  /* Built, the forecast is what happened: the same figures, in the past tense. */
  const built = done.declaredPercent >= 100;
  return tile(
    'programme',
    'Programme',
    `${done.declaredPercent}% complete`,
    fact('Against the baseline', tag(slipWord(forecast.slipDays), forecast.slipDays > 0 ? 3 : 1, `${forecast.slipWorkingDays} working days`)),
    fact(built ? 'Finished' : 'Forecast completion', prettyDate(forecast.forecastFinish)),
    fact('Contract completion', contract === null ? '—' : prettyDate(contract)),
    fact('Baseline', s.baseline.name),
  );
}

/* --------------------------- open on site --------------------------- */

function siteTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey): HTMLElement {
  if (key === 'planned') return tile('site', 'Open on site', unanswered('Not started'));
  const read = siteRecord(b, s);
  const empty = read === true && s.incidents.length === 0 && s.items.length === 0;
  if (noWorks(b, key) && (read !== true || empty)) return tile('site', 'Open on site', unanswered('No works'));
  if (read !== true) return tile('site', 'Open on site', read);
  if (key !== 'construction' && empty) {
    return tile('site', 'Open on site', unanswered('No site record'));
  }
  const incidents = s.incidents.filter((i) => !i.closed).length;
  const ncrs = s.items.filter((i) => i.kind === 'ncr' && i.status !== 'closed').length;
  const holds = s.items.filter((i) => i.kind === 'hold-point' && i.status !== 'closed').length;
  const rfis = s.items.filter((i) => i.kind === 'rfi' && i.status === 'open').length;
  const open = incidents + ncrs + holds + rfis;
  return tile(
    'site',
    'Open on site',
    open === 0 ? 'None open' : `${open} open`,
    fact('Incidents', String(incidents)),
    fact('NCRs', String(ncrs)),
    fact('Hold points', String(holds)),
    fact('RFIs unanswered', String(rfis)),
  );
}

/* --------------------------- handover and model of record --------------------------- */

function handoverTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey): HTMLElement {
  const mor = b.modelOfRecord ?? null;
  const morName = mor ? (s.models.models.find((m) => m.id === mor.modelId)?.name ?? mor.modelId) : null;
  /* The works on a bridge built (LC-81, LC-82): open, the last completed, or none. */
  const works =
    key === 'planned' || key === 'construction'
      ? null
      : fact(
          'Works',
          b.works
            ? `${WORKS_KIND_LABEL[b.works.kind]} · open`
            : b.worksCompleted
              ? `Completed ${prettyDate(b.worksCompleted.completedOn)}`
              : 'None',
        );
  const model = [fact('Model of record', mor ? `${morName} · ${mor.kind}` : 'None set'), works];
  const ho = b.handover ?? null;
  if (ho !== null) {
    return tile(
      'handover',
      'Handover',
      tag('Accepted', 1),
      fact('Completed', prettyDate(ho.completedOn)),
      fact('Accepted by', ho.acceptedBy ?? '—'),
      fact('Defects liability', ho.dlpEndsOn ? `Until ${prettyDate(ho.dlpEndsOn)}` : '—'),
      ...model,
    );
  }
  if (key === 'planned') return tile('handover', 'Handover', unanswered('Not built'), ...model);
  if (key === 'construction') {
    const status = s.handover === null ? null : (s.handover.handover?.status ?? 'none');
    return tile(
      'handover',
      'Handover',
      status === null ? unanswered('Not read') : status === 'none' ? unanswered('Not prepared') : tag(HANDOVER_STATUS_LABEL[status], 2),
      ...model,
    );
  }
  /* A standing structure taken on to the register, not handed over through it. */
  return tile('handover', 'Handover', unanswered('None on file'), ...model);
}

/* --------------------------- the 3D model --------------------------- */

/**
 * The model the record draws, and the two acts on it: import a CSiBridge
 * export, and delete a saved model. They are here rather than on Inspection's
 * ribbon because the model belongs to the structure, not to one application.
 */
function modelTile(b: BridgeView, s: Readonly<State>): HTMLElement {
  const models = b.id === s.selectedId ? s.models.models : [];
  const shown = heroModel(models) ?? models[0] ?? null;
  const add = modelImportCommand(s);
  const remove = modelDeleteCommand(s);
  const may = can(s, 'model.manage');
  return panel(
    { title: '3D model', class: ['bdash__tile', 'bdash__tile--model'] },
    h(
      'div',
      { class: 'bdash__answer' },
      shown ? shown.name : unanswered(s.modelsRead === 'unreachable' ? 'Not reachable' : 'None imported'),
    ),
    h('div', { class: 'det-facts' }, fact('Saved models', String(models.length))),
    add === null
      ? null
      : h(
          'div',
          { class: 'bdash__actions' },
          button(add.label, {
            sm: true,
            iconPath: ICONS.cube,
            disabled: add.busy || !may,
            title: add.busy || may ? add.title : whyNot(s, 'Importing a 3D model', 'model.manage'),
            onClick: add.run,
          }),
          remove === null
            ? null
            : button('Delete', { sm: true, danger: true, iconPath: ICONS.trash, title: remove.title, onClick: remove.run }),
        ),
  );
}

/* --------------------------- inspection --------------------------- */

function inspectionTile(b: BridgeView, key: PhaseStateKey, standing: InspectionStanding): HTMLElement {
  if (key === 'planned' || key === 'construction') return tile('inspection', 'Inspection', unanswered('Not in service'));
  const worst = standing.worst;
  const latest = standing.latest;
  const accepted = latestAcceptedInspection(b.inspections);
  return tile(
    'inspection',
    'Inspection',
    tag(worst.status, worst.rank, cycleLabel(worst)),
    h(
      'div',
      { class: 'bdash__chips' },
      ...[standing.baseline, standing.special, standing.routine, standing.principal, standing.underwater]
        .filter((c): c is CycleStanding => c !== null)
        .map((c) => tag(cycleChipLabel(c), c.rank, cycleLabel(c))),
    ),
    fact('Latest', latest ? `${inspectionTypeLabel(latest.type)} · ${prettyDate(latest.date)}` : '—'),
    fact('Condition', accepted ? `${accepted.rating} · ${accepted.scale}` : '—'),
  );
}

/* --------------------------- defects --------------------------- */

function defectsTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey): HTMLElement {
  /* A flaw in a structure still being built is a non-conformance, on the site tile. */
  if (key === 'planned' || key === 'construction') return tile('defects', 'Open issues', unanswered('Not in service'));
  if (s.backend !== 'live' && s.registerDefects.length === 0) return tile('defects', 'Open issues', unanswered('Not read'));
  const open = s.registerDefects.filter((d) => d.bridgeId === b.id && isOpenStatus(d.status));
  return tile(
    'defects',
    'Open issues',
    open.length === 0 ? 'None open' : `${open.length} open`,
    ...[...DEFECT_SEVERITIES].reverse().map((sev) => fact(sev, String(open.filter((d) => d.severity === sev).length))),
  );
}

/* --------------------------- rating and posting --------------------------- */

function ratingTile(b: BridgeView, s: Readonly<State>, key: PhaseStateKey): HTMLElement {
  if (key === 'planned') return tile('rating', 'Rating', unanswered('Not built'));
  const ratings = ratingsOf(b.id);
  if (ratings === null) return tile('rating', 'Rating', unanswered('Not read'));
  const governing = governingRating(ratings, b.id);
  if (governing === null) {
    return tile('rating', 'Rating', unanswered(key === 'construction' ? 'No design check' : 'Never rated'));
  }
  const tonnes = postingTonnes(governing.result);
  const due = ratingDueFor(b, s.registerDefects);
  return tile(
    'rating',
    'Rating',
    /* The rating of this bridge, as an address (LC-31). */
    h('a', {
      class: 'bdash__link',
      href: `#/rating/ratings/${encodeURIComponent(b.id)}?rating=${encodeURIComponent(governing.id)}`,
      title: 'The governing rating, in Bridge Rating',
      text: governing.governingRf === null ? 'Rated' : `RF ${displayRf(governing.governingRf)}`,
    }),
    fact('Governed by', join([governing.governingMethod, governing.governingLevel], ' ') || '—'),
    fact('Posting', tonnes === null ? 'None required' : `${displayTonnes(tonnes)} t`),
    fact('Accepted', join([governing.acceptedBy, governing.acceptedAt ? prettyDate(governing.acceptedAt.slice(0, 10)) : null], ' · ') || '—'),
    fact('Re-rating', due === null ? '—' : (reratingChip(due) ?? '—')),
  );
}

/* --------------------------- the verdict --------------------------- */

/** What a verdict rests on — the first part of `decidedLine`, on its own row. */
function basisOf(status: ReturnType<typeof operationalStatus>): string {
  switch (status.source) {
    case 'rating':
      return status.ratingId ? `Rating ${status.ratingId}` : 'A rating';
    case 'manual':
      return 'Set by hand';
    case 'on-file':
      return 'On file, source not recorded';
    case 'decision': {
      const label = CAUSE_LABEL[status.cause ?? 'decision'];
      return label.charAt(0).toUpperCase() + label.slice(1);
    }
    case null:
      return 'Not recorded';
  }
}

function verdictTile(b: BridgeView): HTMLElement {
  const status = operationalStatus(b);
  return tile(
    'verdict',
    'Verdict',
    h('span', { title: decidedLine(status) }, statusChip(b)),
    fact('Decided by', status.by ?? '—'),
    fact('On', status.at ? prettyDate(status.at.slice(0, 10)) : '—'),
    fact('On the strength of', basisOf(status)),
  );
}

/* --------------------------- the applications --------------------------- */

const MODE: Readonly<Record<'open' | 'read-only' | 'hidden', { label: string; rank: TagRank }>> = {
  open: { label: 'Open', rank: 1 },
  'read-only': { label: 'Read-only', rank: 2 },
  hidden: { label: 'Hidden', rank: 'none' },
};

/** One application: a link where it is offered, the one line saying why where it is not. */
function appRow(s: Readonly<State>, b: BridgeView, app: AppDef): HTMLElement {
  const p = viewPhaseOf(b);
  const id = app.id as Exclude<AppDef['id'], 'home'>;
  const access = appAccess(p, id);
  const line = accessLine(p, id);
  return h(
    'div',
    { class: ['bdash__app', `bdash__app--${access.mode}`] },
    h('span', { class: 'applink__icon' }, icon(app.iconPath, { size: 15 })),
    access.mode === 'hidden'
      ? h('span', { class: 'bdash__appname', text: app.label })
      : h('a', { class: 'bdash__appname bdash__link', href: appHref(s, app.id), text: app.label }),
    tag(MODE[access.mode].label, MODE[access.mode].rank),
    line === null && !access.scope ? null : h('div', { class: 'bdash__why', text: line ?? access.scope ?? '' }),
  );
}

function appsTile(s: Readonly<State>, b: BridgeView): HTMLElement {
  return panel(
    { title: 'Applications', class: ['bdash__tile', 'bdash__tile--apps'] },
    h('div', { class: 'bdash__apps' }, ...PRODUCTS.map((a) => appRow(s, b, a))),
  );
}

/* --------------------------- location --------------------------- */

/**
 * Where the structure stands: the road, the river and the contours around it,
 * drawn by src/map/siteMap.ts into the slot this leaves for it.
 */
function locationTile(b: BridgeView): HTMLElement {
  if (b.lat == null || b.lng == null) return tile('location', 'Location', unanswered('Not located'));
  return panel(
    { title: 'Location', class: ['bdash__tile', 'bdash__tile--location'] },
    h('div', { class: 'bsite' }, h('div', { class: 'bsite__slot', 'data-keep': 'site-map' })),
    h(
      'div',
      { class: 'bsite__foot' },
      h('span', { text: `${b.lat.toFixed(6)}, ${b.lng.toFixed(6)}` }),
      h(
        'span',
        { class: 'bsite__credit' },
        ...SITE_MAP_CREDIT.flatMap((part, i) => [
          i === 0 ? null : h('span', { text: ' | ' }),
          part.href
            ? h('a', { href: part.href, target: '_blank', rel: 'noopener', text: part.text })
            : h('span', { text: part.text }),
        ]),
      ),
    ),
  );
}

/* --------------------------- screen --------------------------- */

export function bridgeDashboard(s: Readonly<State>): HTMLElement {
  const b = findBridge(s.selectedId);
  if (b === null) return emptyState('This register has no structures in it yet.');
  const key = stateKey(phaseOf(b));
  const standing = inspectionStanding(b);
  return h(
    'div',
    { class: 'bdash', 'data-bridge': b.id },
    stateTile(b, s, key, standing),
    verdictTile(b),
    programmeTile(b, s, key),
    siteTile(b, s, key),
    handoverTile(b, s, key),
    modelTile(b, s),
    inspectionTile(b, key, standing),
    defectsTile(b, s, key),
    ratingTile(b, s, key),
    appsTile(s, b),
    locationTile(b),
  );
}
